/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { FilePlus2, Trash2, Download, FileCode, RefreshCw, Receipt, Send, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import {
  listInvoices, cancelInvoice, invoicePdfUrl, invoiceXmlUrl,
  invoicesCsvExportUrl, sendInvoiceToSdi, getPecConfigStatus, getNextInvoiceNumber,
} from '@/lib/invoicing';
import RevenueCharts from '@/components/invoicing/RevenueCharts';
import { InvoiceForm } from '@/components/invoicing/InvoiceForm';
import { euro } from '@/components/invoicing/fiscalPreview';

// Compatibilità: l'anteprima fiscale è ora in components/invoicing/fiscalPreview.js
export { computeFiscalPreview } from '@/components/invoicing/fiscalPreview';

export default function InvoicingPage() {
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nextNumber, setNextNumber] = useState(null);
  const [pecConfigured, setPecConfigured] = useState(false);
  const [sendingSdi, setSendingSdi] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, nxt, pec] = await Promise.all([
        listInvoices({ limit: 100 }),
        getNextInvoiceNumber().catch(() => null),
        getPecConfigStatus().catch(() => ({ configured: false })),
      ]);
      setItems(list.items || []);
      setNextNumber(nxt?.next_number || null);
      setPecConfigured(!!pec?.configured);
    } catch (e) {
      toast.error('Impossibile caricare le fatture');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [acting, setActing] = useState(false);
  const handleCancel = async (id, number) => {
    if (acting || !window.confirm(`Annullare la fattura n° ${number}?`)) return;
    setActing(true);
    try {
      await cancelInvoice(id);
      toast.success('Fattura annullata');
      await load();
    } catch {
      toast.error('Errore annullamento');
    } finally { setActing(false); }
  };

  const handleSendSdi = async (id, number) => {
    if (!window.confirm(`Inviare la fattura ${number} al Sistema di Interscambio via PEC?`)) return;
    setSendingSdi(id);
    try {
      const res = await sendInvoiceToSdi(id);
      toast.success(`Inviata a ${res.to} (${res.transport_id?.slice(0, 24)}…)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invio PEC fallito');
    } finally {
      setSendingSdi(null);
    }
  };

  if (user && (user.category === 'paziente')) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Il modulo fatturazione è riservato ai professionisti.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="invoicing-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Receipt className="h-7 w-7 text-primary" /> Fatturazione
          </h1>
          <p className="text-sm text-muted-foreground">
            Emetti fatture PDF con numerazione progressiva annuale, IVA calcolata automaticamente e IBAN opzionale.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {nextNumber && (
            <Badge variant="outline" className="font-mono" data-testid="next-invoice-number">
              Prossimo n° {nextNumber}
            </Badge>
          )}
          <a
            href={invoicesCsvExportUrl({ year: new Date().getFullYear() })}
            target="_blank"
            rel="noreferrer"
            data-testid="export-csv-btn"
          >
            <Button variant="outline" size="sm" title="Esporta CSV anno corrente per il commercialista">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Esporta CSV
            </Button>
          </a>
          <Button variant="outline" size="icon" onClick={load} aria-label="Aggiorna">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-invoice-btn"><FilePlus2 className="h-4 w-4 mr-2" /> Nuova fattura</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Nuova fattura</DialogTitle></DialogHeader>
              <InvoiceForm onClose={() => setDialogOpen(false)} onSaved={load} />
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>

      <RevenueCharts />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fatture emesse</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {['a', 'b', 'c', 'd'].map((k) => <Skeleton key={`inv-skel-${k}`} className="h-10 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nessuna fattura ancora. Clicca <b>Nuova fattura</b> per emettere la prima.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N°</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`invoice-row-${it.number.replace('/', '-')}`}>
                    <TableCell className="font-mono text-xs">{it.number}</TableCell>
                    <TableCell>{it.customer?.full_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{it.issued_at_date}</TableCell>
                    <TableCell className="text-right font-semibold">{euro(it.totals?.total)}</TableCell>
                    <TableCell>
                      {it.status === 'cancelled'
                        ? <Badge variant="destructive">Annullata</Badge>
                        : <Badge variant="outline" className="border-emerald-500 text-emerald-700">Emessa</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <a href={invoicePdfUrl(it.id)} target="_blank" rel="noreferrer" data-testid={`invoice-download-${it.id}`}>
                          <Button variant="ghost" size="icon" aria-label="Scarica PDF">
                            <Download className="h-4 w-4" />
                          </Button>
                        </a>
                        <a href={invoiceXmlUrl(it.id)} target="_blank" rel="noreferrer" data-testid={`invoice-xml-${it.id}`}>
                          <Button variant="ghost" size="icon" aria-label="Scarica XML FatturaPA per SDI" title="Scarica XML FatturaPA (SDI)">
                            <FileCode className="h-4 w-4 text-primary" />
                          </Button>
                        </a>
                        {pecConfigured && it.status !== 'cancelled' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSendSdi(it.id, it.number)}
                            disabled={sendingSdi === it.id}
                            aria-label="Invia allo SDI via PEC"
                            title={it.sdi_status === 'sent' ? 'Già inviata via PEC — invia di nuovo' : 'Invia allo SDI via PEC'}
                            data-testid={`invoice-send-sdi-${it.id}`}
                          >
                            <Send className={`h-4 w-4 ${it.sdi_status === 'sent' ? 'text-emerald-600' : 'text-primary'} ${sendingSdi === it.id ? 'animate-pulse' : ''}`} />
                          </Button>
                        )}
                        {it.status !== 'cancelled' && (
                          <Button variant="ghost" size="icon" onClick={() => handleCancel(it.id, it.number)} disabled={acting} aria-label="Annulla fattura" data-testid={`invoice-cancel-${it.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
